import { isPreview } from "../lib/preview";
import { useEffect, useId, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  ArrowRight,
  BookOpen,
  CheckCheck,
  ChevronDown,
  CirclePlay,
  FileText,
  GraduationCap,
  ListChecks,
  PartyPopper,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  Label,
  PageHeader,
  Progress,
  Skeleton,
} from "@codelittinc/carbon-design-system";
import type { TrackAssignedVia, TrainingItem, TrainingTrack } from "@carbon-os/server/contract";
import {
  ICON,
  NotConnected,
  NotInGatekeeper,
  SignInAgain,
  Unavailable,
  useRetryFocus,
  WrongAccount,
  type FocusRef,
} from "../components/Panels";
import { Quiz } from "../components/Quiz";
import { STEP_RESOURCES, TrackPlayer, resourceOf, stepKind, type StepResource } from "../components/TrackPlayer";
import { trackApp, trackTraining, type TrainingEvents } from "../lib/analytics";
import { canTakeQuiz, quizRowState, useQuiz, type QuizRowState } from "../lib/quiz";
import { isHttpUrl } from "../lib/tools";
import {
  celebrate,
  firstUnfinished,
  isComplete,
  newlyCompleted,
  overall,
  progressOf,
  dueLabel,
  formatEndDay,
  trackEnded,
  countsFor,
  upNext,
  type Done,
  type TrainingControls,
} from "../lib/training";

const ITEM_ACTION = "h-11 w-full sm:h-8 sm:w-auto";

type OpenedVia = TrainingEvents["training_player_opened"]["via"];

/** Gatekeeper marks an uploaded video done at this much watched, so below it a started one is resumed. */
const WATCH_DONE_PERCENT = 90;

/** 754 → "12:34", 3725 → "1:02:05". */
export function clock(totalSeconds: number): string {
  const total = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}` : `${minutes}:${seconds}`;
}

export function Training({ training }: { training: TrainingControls }) {
  const { state, retry, signIn } = training;
  const { focusRef, onRetry } = useRetryFocus(state, retry);

  // Only this page sends anybody to sign in for training; Home leaves it to the tool list.
  useEffect(() => {
    if (state.kind === "needs_signin") signIn();
  }, [state.kind, signIn]);

  switch (state.kind) {
    case "loading":
    case "needs_signin":
      return <Loading />;
    case "ready":
      return (
        <Ready tracks={state.tracks} done={state.done} viewerKey={state.viewerKey} training={training} focusRef={focusRef} />
      );
    case "unavailable":
      return (
        <Unavailable
          title="We can't reach your training right now"
          description="Your progress is safe. Try again in a moment."
          retrying={state.retrying}
          retry={() => {
            trackApp("retry_clicked", { area: "training" });
            onRetry();
          }}
          focusRef={focusRef}
        />
      );
    case "signin_failed":
      return <SignInAgain focusRef={focusRef} area="training" />;
    case "wrong_account":
      return <WrongAccount signedInAs={state.signedInAs} signOutUrl={state.signOutUrl} focusRef={focusRef} />;
    case "not_in_gatekeeper":
      return <NotInGatekeeper signedInAs={state.signedInAs} signOutUrl={state.signOutUrl} accessRequestUrl={null} focusRef={focusRef} />;
    case "not_connected":
      return <NotConnected message={state.message} focusRef={focusRef} />;
  }
}

function Loading() {
  return (
    <div>
      <p role="status" className="sr-only">
        Loading your training
      </p>
      <div className="mb-6">
        <Skeleton className="h-8 w-64 max-w-full motion-reduce:animate-none" />
        <Skeleton className="mt-2 h-4 w-48 max-w-full motion-reduce:animate-none" />
      </div>
      <div className="flex flex-col gap-4">
        {Array.from({ length: 2 }, (_, i) => (
          <Skeleton key={i} className="h-40 rounded-lg motion-reduce:animate-none" />
        ))}
      </div>
    </div>
  );
}

function summary(tracks: TrainingTrack[], done: Done): string {
  const { done: count, total } = overall(tracks, done);
  return `${count} of ${total} done across ${tracks.length === 1 ? "1 track" : `${tracks.length} tracks`}`;
}

function Ready({
  tracks,
  done,
  viewerKey,
  training,
  focusRef,
}: {
  tracks: TrainingTrack[];
  done: Done;
  viewerKey: string | null;
  training: TrainingControls;
  focusRef: FocusRef;
}) {
  const [params, setParams] = useSearchParams();
  // Home's nudge names the track to continue.
  const asked = tracks.find((track) => track.id === params.get("track")) ?? null;
  // Decided once, when the page first has its tracks: a tick never opens or
  // closes a track by itself, and opening one never closes another.
  const [open, setOpen] = useState<Set<string>>(() => {
    const first = asked ?? firstUnfinished(tracks, done);
    return new Set(first ? [first.id] : []);
  });
  const [announcement, setAnnouncement] = useState("");
  const headings = useRef(new Map<string, HTMLElement>());
  const quizButtons = useRef(new Map<string, HTMLElement>());
  // Where focus goes back to when the quiz closes: its row's button, or the
  // track's heading once that button is gone.
  const quizFrom = useRef<{ trackId: string; materialId: string } | null>(null);
  const quiz = useQuiz(training);
  const resource = params.get("resource");
  const resourceId = params.get("item");
  const resourceTrack = params.get("track");
  const openedFile = useRef<string | null>(null);
  const clearResource = () => {
    const next = new URLSearchParams(params);
    next.delete("resource");
    next.delete("item");
    setParams(next, { replace: true });
  };
  const [watch, setWatch] = useState<{ trackId: string; item: TrainingItem; quiz: boolean } | null>(null);
  const openedResource = useRef<string | null>(null);
  const watchButtons = useRef(new Map<string, HTMLElement>());
  const videoFrom = useRef<{ trackId: string; materialId: string } | null>(null);
  // Set while the player is open, so an opening counts once and its close says how long it was up.
  const playerOpened = useRef<{ trackId: string; at: number } | null>(null);
  // The step a click just put in the address bar. The router applies it in a
  // transition, after the click's own render, so until it lands an address
  // without a step is the address catching up, not the browser's Back.
  const awaitedResource = useRef<string | null>(null);
  const openPlayer = (track: TrainingTrack, item: TrainingItem, quiz: boolean, via: OpenedVia) => {
    if (!playerOpened.current) {
      playerOpened.current = { trackId: track.id, at: performance.now() };
      trackTraining("training_player_opened", {
        track_id: track.id,
        material_id: item.materialId,
        step_kind: stepKind(item, quiz),
        via,
      });
    }
    setWatch({ trackId: track.id, item, quiz });
  };
  const closePlayer = () => {
    const opened = playerOpened.current;
    if (!opened) return;
    playerOpened.current = null;
    trackTraining("training_player_closed", { track_id: opened.trackId, duration_ms: Math.round(performance.now() - opened.at) });
  };
  // The file open in its viewer, from a click or a link: only a real file
  // item, so a link to anything else opens nothing.
  const fileTrack = resource === "file" ? tracks.find((candidate) => candidate.id === resourceTrack) : undefined;
  const fileItem = fileTrack?.items.find((candidate) => candidate.materialId === resourceId);
  const openFile =
    fileTrack && fileItem && fileItem.itemType !== "process" && fileItem.kind === "file" && !fileItem.video && fileItem.url
      ? { trackId: fileTrack.id, materialId: fileItem.materialId }
      : null;
  const openFileTrack = openFile?.trackId ?? null;
  const openFileItem = openFile?.materialId ?? null;
  // Once per opening, however many times the page renders while it is open.
  useEffect(() => {
    if (!openFileTrack || !openFileItem) { openedFile.current = null; return; }
    const key = `${openFileTrack}:${openFileItem}`;
    if (openedFile.current === key) return;
    openedFile.current = key;
    trackTraining("training_item_opened", { material_id: openFileItem, track_id: openFileTrack, kind: "file" });
  }, [openFileTrack, openFileItem]);
  useEffect(() => {
    if (!resourceId || !resourceTrack || !STEP_RESOURCES.includes(resource as StepResource)) {
      openedResource.current = null;
      if (watch) {
        // The browser's Back closed it.
        if (awaitedResource.current === null) closePlayer();
        setWatch(null);
      }
      return;
    }
    if (awaitedResource.current === `${resource}:${resourceTrack}:${resourceId}`) awaitedResource.current = null;
    const track = tracks.find((candidate) => candidate.id === resourceTrack);
    const item = track?.items.find((candidate) => candidate.materialId === resourceId);
    if (!track || !item) return;
    const isQuiz = resource === "quiz";
    if (isQuiz && !canTakeQuiz(item)) return;
    // A link names the kind it opens: a stale one for another kind opens nothing.
    if (!isQuiz && resourceOf(item) !== resource) return;
    const key = `${resource}:${track.id}:${item.materialId}`;
    if (openedResource.current === key) return;
    openedResource.current = key;
    videoFrom.current = { trackId: track.id, materialId: item.materialId };
    if (isQuiz) quizFrom.current = { trackId: track.id, materialId: item.materialId };
    openPlayer(track, item, isQuiz, "link");
  }, [tracks, resource, resourceId, resourceTrack, watch]);
  useEffect(() => {
    if (!watch || tracks.some((track) => track.id === watch.trackId)) return;
    // A refresh took its track away, and the player with it.
    closePlayer();
    setWatch(null);
  }, [tracks, watch]);
  // Leaving the page with the player open closes it too. StrictMode's
  // mount-unmount-mount puts `mounted` back before the check runs, so only a
  // real unmount counts.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      queueMicrotask(() => {
        if (!mounted.current) closePlayer();
      });
    };
  }, []);
  // Refs survive StrictMode's mount-unmount-mount, so each runs once per visit.
  const viewed = useRef(false);
  const focused = useRef(false);

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    const { done: count, total } = overall(tracks, done);
    trackTraining("training_viewed", { track_count: tracks.length, items_done: count, items_total: total });
  }, [tracks, done]);

  useEffect(() => {
    if (focused.current || !asked) return;
    focused.current = true;
    headings.current.get(asked.id)?.focus();
  }, [asked]);

  const header = (description?: string) => (
    <PageHeader
      title={
        <span ref={focusRef} tabIndex={-1} className="outline-none">
          Your training
        </span>
      }
      {...(description ? { description } : {})}
    />
  );

  if (tracks.length === 0) {
    return (
      <>
        {header()}
        <EmptyState
          icon={<GraduationCap className={ICON} />}
          title="No training assigned yet"
          description="When your team sets some up, it'll appear here."
        />
      </>
    );
  }

  const onToggle = (track: TrainingTrack, item: TrainingItem) => {
    const wasDone = Boolean(done[item.materialId]);
    const next: Done = { ...done, [item.materialId]: wasDone ? null : "now" };
    training.toggle(item.materialId, track.id);

    const { done: count, total } = progressOf(track, next);
    setAnnouncement(
      `${item.title} marked ${wasDone ? "not done" : "done"}. ${count} of ${total} done in ${track.name}.`,
    );
    if (wasDone) return;
    // One material can finish several tracks at once, wherever it was ticked.
    for (const finished of newlyCompleted(tracks, item.materialId, done, next)) celebrate(finished);
  };

  const onQuiz = (track: TrainingTrack, item: TrainingItem, via: OpenedVia) => {
    if (!canTakeQuiz(item)) return;
    quizFrom.current = { trackId: track.id, materialId: item.materialId };
    videoFrom.current = { trackId: track.id, materialId: item.materialId };
    openedResource.current = `quiz:${track.id}:${item.materialId}`;
    awaitedResource.current = openedResource.current;
    openPlayer(track, item, true, via);
    setParams({ track: track.id, item: item.materialId, resource: "quiz" });
  };

  const onQuizClosed = (event: Event) => {
    event.preventDefault();
    clearResource();
    const from = quizFrom.current;
    if (!from) return;
    const gone = quiz.view.kind === "closed" && quiz.view.gone;
    const button = gone ? undefined : quizButtons.current.get(`${from.trackId}:${from.materialId}`);
    (button ?? headings.current.get(from.trackId))?.focus();
  };

  /**
   * Opens the track player on one step — every row's button, Start and
   * Continue, and a link. Focus comes back to the row's button on close.
   */
  const onOpen = (track: TrainingTrack, item: TrainingItem, via: OpenedVia) => {
    const kind = resourceOf(item);
    videoFrom.current = { trackId: track.id, materialId: item.materialId };
    openedResource.current = `${kind}:${track.id}:${item.materialId}`;
    awaitedResource.current = openedResource.current;
    openPlayer(track, item, false, via);
    setParams({ track: track.id, item: item.materialId, resource: kind });
  };

  /**
   * Start or Continue on a card: the track opens, and so does its next step in
   * the player — the quiz, when that is what is left of a watched video.
   */
  const onStart = (track: TrainingTrack, item: TrainingItem, via: "start" | "continue") => {
    setOpen((current) => new Set(current).add(track.id));
    if (item.video && item.quiz && item.video.watchedPercent >= WATCH_DONE_PERCENT && canTakeQuiz(item)) onQuiz(track, item, via);
    else onOpen(track, item, via);
  };

  /** The player moved to another step: the address bar follows, without reopening it. */
  const onStepChange = (track: TrainingTrack, item: TrainingItem, kind: StepResource) => {
    openedResource.current = `${kind}:${track.id}:${item.materialId}`;
    setParams({ track: track.id, item: item.materialId, resource: kind }, { replace: true });
  };

  const onVideoClosed = (event: Event) => {
    event.preventDefault();
    clearResource();
    const from = videoFrom.current;
    if (!from) return;
    (watchButtons.current.get(`${from.trackId}:${from.materialId}`) ?? headings.current.get(from.trackId))?.focus();
  };

  const allDone = tracks.every((track) => isComplete(track, done));
  const watchTrack = watch ? tracks.find((track) => track.id === watch.trackId) : null;

  return (
    <>
      {header(summary(tracks, done))}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {allDone ? (
        <EmptyState
          icon={<PartyPopper className={ICON} />}
          title="You're all caught up"
          description="Every track is done. New training will show up here."
          className="py-8"
        />
      ) : null}
      <ol aria-label="Your training tracks" className="flex flex-col gap-4">
        {tracks.map((track, index) => (
          <li key={track.id} className="min-w-0">
            <TrackCard
              track={track}
              number={index + 1}
              done={done}
              open={open.has(track.id)}
              onOpenChange={() =>
                setOpen((current) => {
                  const next = new Set(current);
                  if (next.has(track.id)) next.delete(track.id);
                  else next.add(track.id);
                  return next;
                })
              }
              headingRef={(element) => {
                if (element) headings.current.set(track.id, element);
                else headings.current.delete(track.id);
              }}
              onToggle={(item) => onToggle(track, item)}
              onStart={(item, started) => onStart(track, item, started ? "continue" : "start")}
              onQuiz={(item) => onQuiz(track, item, "quiz_row")}
              quizRef={(materialId, element) => {
                const key = `${track.id}:${materialId}`;
                if (element) quizButtons.current.set(key, element);
                else quizButtons.current.delete(key);
              }}
              onOpen={(item) => onOpen(track, item, "row")}
              watchRef={(materialId, element) => {
                const key = `${track.id}:${materialId}`;
                if (element) watchButtons.current.set(key, element);
                else watchButtons.current.delete(key);
              }}
            />
          </li>
        ))}
      </ol>
      <Quiz quiz={quiz} onCloseAutoFocus={onQuizClosed} />
      {watch && watchTrack ? (
        <TrackPlayer track={watchTrack} initialItem={watch.item}
          initialQuiz={watch.quiz} done={done} training={training} viewerKey={viewerKey}
          onClose={() => { closePlayer(); setWatch(null); clearResource(); }} onCloseAutoFocus={watch.quiz ? onQuizClosed : onVideoClosed}
          onToggle={(item) => onToggle(watchTrack, item)}
          onStepChange={(item, kind) => onStepChange(watchTrack, item, kind)}
          onProcessAcknowledged={training.refresh} />
      ) : null}
    </>
  );
}

/**
 * How the track reached them, one tone each — the same tones Gatekeeper's
 * admin pages use: accent for their department, info for their job title,
 * neutral for one assigned to them by hand. The words carry it too, so colour
 * is never the only cue.
 */
const VIA_BADGE: Record<TrackAssignedVia, { label: string; variant: "accent" | "info" | "default" }> = {
  department: { label: "Your department", variant: "accent" },
  title: { label: "Your job title", variant: "info" },
  manual: { label: "Assigned to you", variant: "default" },
};

function TrackViaBadge({ via }: { via: TrackAssignedVia | null }) {
  if (via === null) return null;
  const { label, variant } = VIA_BADGE[via];
  return <Badge variant={variant}>{label}</Badge>;
}

function TrackCard({
  track,
  number,
  done,
  open,
  onOpenChange,
  headingRef,
  onToggle,
  onStart,
  onQuiz,
  quizRef,
  onOpen,
  watchRef,
}: {
  track: TrainingTrack;
  /** Its place in Gatekeeper's order, from 1: department, job title, then by hand. */
  number: number;
  done: Done;
  open: boolean;
  onOpenChange: () => void;
  headingRef: (element: HTMLElement | null) => void;
  onToggle: (item: TrainingItem) => void;
  /** Opens the track and its next step; `started` says whether it showed Continue. */
  onStart: (item: TrainingItem, started: boolean) => void;
  onQuiz: (item: TrainingItem) => void;
  quizRef: (materialId: string, element: HTMLElement | null) => void;
  /** Opens the track player on this item. */
  onOpen: (item: TrainingItem) => void;
  watchRef: (materialId: string, element: HTMLElement | null) => void;
}) {
  const listId = useId();
  const { done: count, total } = progressOf(track, done);
  const complete = isComplete(track, done);
  const next = upNext(track, done);
  const nextItem = track.items.find((item) => item.materialId === next);
  // Begun: anything done, any video watched a little, any quiz tried or under way.
  const started = count > 0 || track.items.some((item) =>
    (item.video?.watchedPercent ?? 0) > 0 || (item.quiz?.attempts ?? 0) > 0 || item.quiz?.inProgress != null);

  return (
    <Card padding="md">
      <CardHeader
        as="h2"
        className="mb-3"
        title={
          <span className="flex items-center gap-3">
            {/* Shown, not read: the list is an <ol>, so a screen reader already says "1 of 6". */}
            <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-full bg-bg text-sm tabular-nums">
              {number}
            </span>
            <span ref={headingRef} tabIndex={-1} className="min-w-0 outline-none">
              {track.name}
            </span>
          </span>
        }
        description={track.description ? <span className="line-clamp-3">{track.description}</span> : undefined}
        actions={
          <>
            <TrackViaBadge via={track.assignedVia} />
            {complete ? (
              <Badge variant="success">Completed</Badge>
            ) : trackEnded(track) ? (
              <Badge variant="error">Ended</Badge>
            ) : track.endsAt && track.status === "due_soon" ? (
              <Badge variant="warning">{dueLabel(track.endsAt)}</Badge>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 sm:h-8 sm:w-8"
              aria-expanded={open}
              aria-controls={listId}
              aria-label={`${open ? "Hide" : "Show"} items in ${track.name}`}
              onClick={onOpenChange}
            >
              <ChevronDown
                aria-hidden
                className={`size-4 motion-safe:transition-transform ${open ? "rotate-180" : ""}`}
              />
            </Button>
          </>
        }
      />
      <Progress
        value={count}
        max={total}
        tone={complete ? "success" : "accent"}
        aria-label={`${track.name} progress`}
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="min-w-0 text-sm text-text-secondary">
          {count} of {total} done
          {track.endsAt && !complete ? (
            <>
              {" · "}
              {trackEnded(track)
                ? `Ended ${formatEndDay(track.endsAt)}. You can still open its items; ask an admin if you need more time.`
                : `Finish by ${formatEndDay(track.endsAt)}`}
            </>
          ) : null}
        </p>
        {nextItem ? (
          <Button className="h-11 shrink-0 sm:h-9" onClick={() => onStart(nextItem, started)} aria-label={`${started ? "Continue" : "Start"} ${track.name}`}>
            {started ? "Continue" : "Start"}
            <ArrowRight className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>
      <ol id={listId} hidden={!open} aria-label={`Items in ${track.name}`} className="mt-3">
        {track.items.map((item) => (
          <ItemRow
            key={item.materialId}
            item={item}
            trackId={track.id}
            done={Boolean(done[item.materialId])}
            late={Boolean(done[item.materialId]) && !countsFor(track, done[item.materialId])}
            upNext={item.materialId === next}
            quiz={quizRowState(item.quiz, done[item.materialId] ?? null)}
            onToggle={() => onToggle(item)}
            onQuiz={() => onQuiz(item)}
            quizRef={(element) => quizRef(item.materialId, element)}
            onOpen={() => onOpen(item)}
            watchRef={(element) => watchRef(item.materialId, element)}
          />
        ))}
      </ol>
    </Card>
  );
}

/** The quiz button's words for each state that has one. */
const QUIZ_ACTION: Partial<Record<QuizRowState["kind"], string>> = {
  not_started: "Take quiz",
  in_progress: "Resume quiz",
  failed: "Try again",
};

/** The row's one line about its quiz, in words, never in colour alone. */
function QuizStatus({ quiz }: { quiz: QuizRowState }) {
  const dot = <span aria-hidden>·</span>;
  switch (quiz.kind) {
    case "none":
      return null;
    case "not_started":
      return (
        <>
          <span>Quiz</span>
          {dot}
          <span>{quiz.questionCount === 1 ? "1 question" : `${quiz.questionCount} questions`}</span>
          {dot}
          <span>pass mark {quiz.passPercent}%</span>
        </>
      );
    case "in_progress":
      return (
        <>
          <span>In progress</span>
          {dot}
          <span>
            {quiz.answered} of {quiz.total} answered
          </span>
        </>
      );
    case "passed":
      // Done before the quiz existed, or without a passing score on record: no percent to show.
      return quiz.bestPercent === null ? (
        <Badge variant="success">Done</Badge>
      ) : (
        <>
          <Badge variant="success">Passed</Badge>
          <span>best {quiz.bestPercent}%</span>
        </>
      );
    case "failed":
      return (
        <>
          <span>Best {quiz.bestPercent}%</span>
          {dot}
          <span>need {quiz.passPercent}%</span>
        </>
      );
  }
}

function ItemRow({
  item,
  trackId,
  done,
  late = false,
  upNext,
  quiz,
  onToggle,
  onQuiz,
  quizRef,
  onOpen,
  watchRef,
}: {
  item: TrainingItem;
  trackId: string;
  done: boolean;
  /** Done, but after this track ended, so it does not count in it. */
  late?: boolean;
  upNext: boolean;
  quiz: QuizRowState;
  onToggle: () => void;
  onQuiz: () => void;
  quizRef: (element: HTMLElement | null) => void;
  /** Opens the track player on this item: every kind opens there. */
  onOpen: () => void;
  watchRef: (element: HTMLElement | null) => void;
}) {
  const checkboxId = useId();
  const detailsId = useId();
  const noLinkId = useId();
  // Opening is not finishing: the row only suggests the tick, never makes it.
  const [opened, setOpened] = useState(false);
  if (item.itemType === "process") {
    return (
      <li className="flex flex-wrap items-center gap-3 border-t border-border-subtle py-3">
        <Checkbox checked={done} disabled aria-label={`${item.title}: ${done ? "read" : "needs reading"}`} />
        {upNext ? <Badge variant="default">Up next</Badge> : null}
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium ${done ? "text-text-muted" : "text-text-primary"}`}>{item.title}</p>
          <p className="text-xs text-text-faint">Company handbook · version {item.version}{item.needsReread && item.previousVersion ? ` · updated from version ${item.previousVersion}` : ""}</p>
        </div>
        {!done && !item.locked && <Badge variant="default">{item.needsReread && item.previousVersion ? "Updated · read again" : "To read"}</Badge>}
        {!done && item.locked && <Badge variant="default">Track ended</Badge>}
        <Button ref={watchRef} variant={item.needsReread ? "outline" : "ghost"} size="sm" className="h-11 sm:h-8" onClick={onOpen}>
          <BookOpen className="size-4" aria-hidden />{item.needsReread ? "Read process" : "Review process"}
        </Button>
      </li>
    );
  }
  const video = item.kind === "video";
  const link = video && isHttpUrl(item.url) ? item.url : null;
  const KindIcon = video ? CirclePlay : FileText;
  // A material with a quiz is marked done by passing it, never by a tick.
  const quizzed = quiz.kind !== "none";
  const quizAction = canTakeQuiz(item) ? QUIZ_ACTION[quiz.kind] : undefined;

  let action;
  if (item.video) {
    // Uploaded: it plays here, from the bucket, and resumes where it stopped.
    const resume = item.video.watchedPercent > 0 && item.video.watchedPercent < WATCH_DONE_PERCENT;
    action = (
      <Button
        ref={watchRef}
        variant="outline"
        className={ITEM_ACTION}
        aria-label={`${resume ? "Resume" : "Watch"}: ${item.title}`}
        // Not `opened`: watching reports itself, so the row never suggests ticking a video nobody watched.
        onClick={onOpen}
      >
        <CirclePlay className="size-4" aria-hidden />
        {resume ? "Resume" : "Watch"}
      </Button>
    );
  } else if (!video && item.url) {
    // In the track player, beside the steps; its own Download covers what it can't show.
    action = (
      <Button ref={watchRef} variant="outline" className={ITEM_ACTION} aria-label={`Open: ${item.title}`}
        onClick={() => { setOpened(true); onOpen(); }}>
        <FileText className="size-4" aria-hidden />
        Open
      </Button>
    );
  } else if (!video) {
    action = (
      <>
        <Button variant="outline" className={ITEM_ACTION} disabled aria-describedby={noLinkId}>
          <FileText className="size-4" aria-hidden />
          Open
        </Button>
        <p id={noLinkId} className="mt-1 text-xs text-text-secondary">
          This file isn't available
        </p>
      </>
    );
  } else if (link) {
    // In the track player too, where Open video takes it to its own site.
    action = (
      <Button ref={watchRef} variant="outline" className={ITEM_ACTION} aria-label={`Open video: ${item.title}`}
        onClick={() => { setOpened(true); onOpen(); }}>
        <CirclePlay className="size-4" aria-hidden />
        Open video
      </Button>
    );
  } else {
    action = (
      <>
        <Button variant="outline" className={ITEM_ACTION} disabled aria-describedby={noLinkId}>
          Open video
        </Button>
        <p id={noLinkId} className="mt-1 text-xs text-text-secondary">
          This video's link isn't available
        </p>
      </>
    );
  }

  return (
    <li
      className={`flex flex-col gap-2 border-t border-border-subtle px-2 py-3 sm:flex-row sm:items-center sm:gap-4 ${upNext ? "rounded-md bg-accent-muted" : ""}`}
    >
      <div className="min-w-0 flex-1">
        {quizzed ? (
          <p className="flex min-h-11 items-start gap-3 py-1 text-sm font-medium text-text-primary sm:min-h-0">
            <ListChecks className="mt-0.5 size-4 shrink-0 text-text-secondary" aria-hidden />
            <span className={`line-clamp-2 break-words ${done ? "text-text-secondary line-through" : ""}`}>
              {item.title}
            </span>
          </p>
        ) : (
          <Label
            htmlFor={checkboxId}
            className="flex min-h-11 cursor-pointer items-start gap-3 py-1 text-sm font-medium text-text-primary sm:min-h-0"
          >
            <Checkbox
              id={checkboxId}
              checked={done}
              disabled={item.locked || isPreview()}
              onCheckedChange={onToggle}
              aria-describedby={detailsId}
              className="mt-0.5"
            />
            <span className={`line-clamp-2 break-words ${done ? "text-text-secondary line-through" : ""}`}>
              {item.title}
            </span>
          </Label>
        )}
        <p id={detailsId} className="ml-7 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-text-secondary">
          {upNext ? <Badge variant="accent">Up next</Badge> : null}
          <KindIcon className="size-3.5" aria-hidden />
          <span>{video ? "Video" : "File"}</span>
          <span aria-hidden>·</span>
          <span>{item.product.name}</span>
          {done ? (
            <>
              <span aria-hidden>·</span>
              <span>Done</span>
            </>
          ) : null}
          {late ? (
            <>
              <span aria-hidden>·</span>
              <span>Done after this track ended, so it doesn't count here</span>
            </>
          ) : item.locked ? (
            <>
              <span aria-hidden>·</span>
              <span>Track ended: you can still open it, but it can't be marked done</span>
            </>
          ) : null}
        </p>
        {item.video ? (
          <div className="mt-2 ml-7 flex max-w-xs flex-col gap-1">
            <Progress value={item.video.watchedPercent} max={100} aria-label={`${item.title}: watched`} />
            <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-text-secondary">
              <span>{item.video.watchedPercent}% watched</span>
              <span aria-hidden>·</span>
              <span>
                <span className="sr-only">length </span>
                {clock(item.video.durationSeconds)}
              </span>
            </p>
          </div>
        ) : null}
        {quizzed ? (
          <p className="mt-1 ml-7 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-text-secondary">
            <QuizStatus quiz={quiz} />
          </p>
        ) : null}
        {opened && !done && !item.locked ? (
          <p className="mt-1 ml-7 flex items-center gap-1.5 text-xs text-text-secondary">
            <CheckCheck className="size-3.5" aria-hidden />
            {quizzed ? "Done? Take the quiz." : "Finished? Tick it off."}
          </p>
        ) : null}
      </div>
      <div className="ml-7 sm:ml-0 sm:shrink-0">
        {quizAction ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <div>{action}</div>
            <Button
              disabled={isPreview()}
              ref={quizRef}
              // Filled once the material has been opened: the quiz is the next step.
              variant={opened ? "default" : "outline"}
              className={ITEM_ACTION}
              aria-label={`${quizAction}: ${item.title}`}
              onClick={onQuiz}
            >
              {quizAction}
            </Button>
          </div>
        ) : (
          action
        )}
      </div>
    </li>
  );
}
